import { UserRole } from '../types';

export interface RuntimeEnvConfig {
  superAdminEmails: string[];
  ownerEmails: string[];
  hasSuperAdminEnv: boolean;
  hasOwnerEnv: boolean;
}

let cachedRuntimeConfig: RuntimeEnvConfig | null = null;
let fetchPromise: Promise<RuntimeEnvConfig | null> | null = null;

export const fetchRuntimeEnvConfig = async (): Promise<RuntimeEnvConfig | null> => {
  if (cachedRuntimeConfig) return cachedRuntimeConfig;
  if (fetchPromise) return fetchPromise;

  fetchPromise = (async () => {
    try {
      const res = await fetch('/api/config/env');
      if (res.ok) {
        const data = await res.json();
        cachedRuntimeConfig = data;
        return data;
      }
    } catch (e) {
      console.warn('Failed to fetch runtime env config:', e);
    }
    return null;
  })();

  return fetchPromise;
};

export const getSuperAdminEmails = (): string[] => {
  const envVal = (import.meta as any).env?.VITE_SUPERADMIN_EMAILS;
  const raw = typeof envVal === 'string' ? envVal : '';
  const buildEmails = raw.split(',').map(e => e.trim().toLowerCase()).filter(Boolean);

  const runtimeEmails = cachedRuntimeConfig?.superAdminEmails ?? [];
  return Array.from(new Set([...buildEmails, ...runtimeEmails]));
};

export const getOwnerEmails = (): string[] => {
  const envVal = (import.meta as any).env?.VITE_OWNER_EMAILS;
  const raw = typeof envVal === 'string' ? envVal : '';
  const buildEmails = raw.split(',').map(e => e.trim().toLowerCase()).filter(Boolean);

  const runtimeEmails = cachedRuntimeConfig?.ownerEmails ?? [];
  return Array.from(new Set([...buildEmails, ...runtimeEmails]));
};

export const isSuperAdminEmail = (email?: string | null): boolean => {
  if (!email) return false;
  const clean = email.trim().toLowerCase();
  const list = getSuperAdminEmails();
  return list.includes(clean);
};

export const isOwnerEmail = (email?: string | null): boolean => {
  if (!email) return false;
  const clean = email.trim().toLowerCase();
  const list = getOwnerEmails();
  return list.includes(clean);
};

export const getRoleForEmail = (email?: string | null, fallbackRole: UserRole = 'CLIENT'): UserRole => {
  if (!email) return fallbackRole;
  if (isSuperAdminEmail(email)) return 'SUPER_ADMIN';
  if (isOwnerEmail(email)) return 'OWNER';
  return fallbackRole;
};

